import { Button as AntdButton, Input, Modal, Table } from 'antd';
import { filter } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useAllDistrictTeacher, useCurrentUser } from 'store/users/selectors';
import color from 'utils/constants/color';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { handleUpdateShareList } from 'store/notes/thunks';
import { handleUpdateWorksheetShareList } from 'store/worksheet/thunks';
import { useShareNotification } from 'utils/methods/firebaseShareAssignment';
import { useGetRedirectUrlForBook } from 'containers/hooks';
import moment from 'moment';
import useBookAvailabilityCheck from 'components/hooks/books/useBookAvailabilityCheck';
import SentryWrap from 'components/Sentry';
import { fetchShareListAssignment } from 'store/activities/thunks';
import { useShareList } from 'store/activities/selectors';

const ShareNotesList = ({ shareModal, hideShareModal, shareList, note_id, type = 'note', assignment, bookId, pageId }) => {
  const [searchText, setSearchText] = useState('');
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 8 });
  const [shareLoader, setShareLoader] = useState(false);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const shareListFromStore = useShareList();
  const displayShareList = type === 'worksheet' ? shareListFromStore : shareList;
  const allDistrictTeacher = useAllDistrictTeacher();
  const { createShareNotification } = useShareNotification();
  const { redirectToBook } = useGetRedirectUrlForBook();
  const filterShareBy = useMemo(
    () => filter(allDistrictTeacher, (item) => item?.id !== currentUser.id),
    [allDistrictTeacher, currentUser],
  );
  
  const contentType = type === 'worksheet' ? 'Grammar Drill' : 'Note';
  const checkBookAvailability = useBookAvailabilityCheck(null, contentType);
  
  const checkBookAvailabilityForChapter = (chapterId) => {
    return checkBookAvailability(chapterId, contentType);
  };

  useEffect(() => {
    if (!shareModal) {
      setTablePagination({ current: 1, pageSize: 8 });
      setSearchText('');
    }
  }, [shareModal]);

  const handleInputChange = (text) => {
    setTablePagination((prev) => ({ ...prev, current: 1 }));
    setSearchText(text);
  };

  const handlePaginationChange = (page, size) => {
    setTablePagination((prev) => ({
      current: page,
      pageSize:
        size !== undefined && size !== null ? Number(size) : prev.pageSize,
    }));
  };

  const filterSearchData = useMemo(() => {
    const normalize = (value) =>
      String(value ?? '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '');

    const query = normalize(searchText);
    if (!query) return filterShareBy;

    return filterShareBy?.filter((obj) => {
      const firstName = normalize(obj?.firstName);
      const lastName = normalize(obj?.lastName);
      const fullName = `${firstName}${lastName}`;
      const reverseFullName = `${lastName}${firstName}`;

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        reverseFullName.includes(query)
      );
    });
  }, [searchText, filterShareBy]);

  const handleSend = async (share_id) => {
    try{
      setShareLoader(share_id);
        if (type === 'worksheet') {
          const response = await dispatch(handleUpdateWorksheetShareList(note_id, share_id, currentUser.id));
          if (response?.id) {
            await dispatch(
              fetchShareListAssignment({
                assignmentId: note_id,
                activityType: 'worksheet',
                shareList: response.share_list,
              }),
            );
          }
          if (response && response.id && assignment) {
            const chapterId = bookId || assignment.book_id;
            
            if (!checkBookAvailabilityForChapter(chapterId)) {
              setShareLoader(false);
              return;
            }
            const pageIdValue = pageId || assignment.page_id;
            const url = redirectToBook({
              chapter: chapterId,
              page: pageIdValue
            });
            
            const notificationData = {
              id: response.id,
              display_name: response.display_name || assignment.display_name,
              notification_list: [share_id],
              createdBy: currentUser.id,
              checkBookExist: true,
              chapter: chapterId,
              page: pageIdValue,
              bookId: chapterId,
              pageId: pageIdValue,
              data: {
                chapter: chapterId,
                page: pageIdValue,
                id: response.id,
                assignmentType: 'worksheet'
              },
              message: `${currentUser?.firstName} ${currentUser?.lastName} shared '${response.display_name || assignment.display_name}' Grammar Drill`,
              shared_time: moment().toISOString(),
              url: url || `/ebook?chapter=${chapterId}&page=${pageIdValue}&activity=null&quiz=null`,
            };
            const shareId = assignment.user_id || currentUser.id;
            await createShareNotification(notificationData, shareId, share_id);
          }
        } else {
          const response = await dispatch(handleUpdateShareList(note_id, share_id, currentUser.id));
          if (response && response.id && response.chapter && response.page) {
            const chapterId = bookId || response.chapter;
            
            if (!checkBookAvailabilityForChapter(chapterId)) {
              setShareLoader(false);
              return;
            }
            const pageIdValue = pageId || response.page;
            const url = redirectToBook({
              chapter: chapterId,
              page: pageIdValue
            });
            
            const noteTitle = response.title || 'Note';
            const baseUrl = url || `/ebook?chapter=${chapterId}&page=${pageIdValue}&activity=null&quiz=null`;
            const finalUrl = baseUrl.includes('?') 
              ? `${baseUrl}&noteId=${response.id}`
              : `${baseUrl}?noteId=${response.id}`;
            const notificationData = {
              id: response.id,
              display_name: noteTitle,
              notification_list: [share_id],
              createdBy: currentUser.id,
              checkBookExist: true,
              chapter: chapterId,
              page: pageIdValue,
              bookId: chapterId,
              pageId: pageIdValue,
              data: {
                chapter: chapterId,
                page: pageIdValue,
                id: response.id,
                assignmentType: 'note'
              },
              message: `${currentUser?.firstName} ${currentUser?.lastName} shared ${noteTitle}`,
              shared_time: moment().toISOString(),
              url: finalUrl,
            };
            const shareId = response.userId || currentUser.id;
            await createShareNotification(notificationData, shareId);
          }
        }
    }catch(error){
        console.log(error);
    }finally{
        setShareLoader(false);
    }
  }

  const columns = useMemo(() => [
    {
      title: 'Name',
      key: 'name',
      ellipsis: true,
      width: 148,
      render: (render) => `${render.firstName} ${render.lastName}`,
    },
    {
      title: 'Status',
      key: 'status',
      width: 148,
      align: 'left',
      render: (render) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (!displayShareList?.includes(render?.id)) {
              handleSend(render?.id);
            }
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <AntdButton
              type="primary"
              loading={shareLoader == render?.id ? true : false}
              style={{
                borderRadius: 10,
                minWidth: 108,
                backgroundColor: displayShareList?.includes(render?.id)
                  ? color.greentrue
                  : color.green100,
                border: '0px',
              }}
            >
              {displayShareList?.includes(render?.id) ? 'Shared' : 'Share'}
            </AntdButton>
          </span>
        </div>
      ),
    },
  ], [displayShareList, currentUser.id, shareLoader]);

  return (
    <SentryWrap>
    <Modal open={shareModal} onCancel={hideShareModal} className="activeStudentModal">
      <div
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <InputContainer>
          <StyledInput
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Search District Teacher"
            maxLength={60}
            autoFocus
            value={searchText}
          />
        </InputContainer>
      </div>
      <div>
        <div className="activeStudentMdlContent">
          <TableContainer>
            <CustomTable
              rowKey="id"
              columns={columns}
              dataSource={filterSearchData}
              scroll={{ x: 568, y: 320 }}
              sticky
              pagination={{
                position: 'bottomRight',
                current: tablePagination.current,
                pageSize: tablePagination.pageSize,
                pageSizeOptions: ['8', '10', '20', '50', '100'],
                onChange: handlePaginationChange,
                showSizeChanger: {
                  getPopupContainer: () => document.body,
                  showSearch: false,
                },
              }}
            />
          </TableContainer>
        </div>
      </div>
    </Modal>
    </SentryWrap>
  );
};

export default ShareNotesList;



const CustomTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: rgb(239, 246, 254);
  }
  .ant-table-tbody > tr > td {
    background-color: #fff;
  }
  .ant-table-header table,
  .ant-table-body table {
    table-layout: fixed;
    width: 100% !important;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  background: #fff;
  border-radius: 20px;
  position: relative;
  border: 1px solid;
  margin-top: 2px;
  margin-bottom: 10px;
  max-width: '250px';
  @media (max-width: 600px){
    margin-right: 15px;
  }
`;

const StyledInput = styled(Input)`
  border: none;
  outline: none;
  background: transparent !important;
  &:focus {
    box-shadow: none;
  }
  flex-grow: 1;
`;



const TableContainer = styled.div`
  margin-bottom: 20px;
`;
